import * as React from 'react'
import { Link } from 'react-router-dom'
import Button, { ButtonProps } from '@material-ui/core/Button'
import { LocationDescriptor } from 'history'
import { darkerGray, red } from '../../styles/material-ui-theme-v2'
import { ReactComponent as LoadIcon } from './../../assets/icons/loader.svg'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { Theme, createStyles } from '@material-ui/core'

export interface YumButtonProps extends Omit<ButtonProps, 'color'> {
  to?: LocationDescriptor
  color?: 'inherit' | 'primary' | 'secondary' | 'default' | 'error'
  showLoader?: boolean
  immersiveLoader?: boolean
  // Allow a smaller immersive loader for compact buttons.
  immersiveLoaderSize?: 'default' | 'small'
  darkLoader?: boolean
  // Keep full opacity even when disabled.
  disableDisabledOpacity?: boolean
  children: React.ReactNode
  component?: string
  target?: string
}

// Common style objects
const loaderAnimation = {
  animationName: 'spin',
  animationDuration: '1000ms',
  animationIterationCount: 'infinite',
  animationTimingFunction: 'linear',
}

const sidePosition = {
  position: 'absolute' as const,
  left: '15px',
  top: '12px',
}

const centerPosition = {
  position: 'absolute' as const,
  left: '50%',
  top: '50%',
  marginTop: '-12px !important',
  marginLeft: '-12px',
}

// Smaller centered loader for compact buttons.
const centerPositionSmall = {
  position: 'absolute' as const,
  left: '50%',
  top: '50%',
  marginTop: '-8px !important',
  marginLeft: '-8px',
  width: '16px',
  height: '16px',
}

const lightColor = {
  '& path': {
    fill: 'white',
  },
}

const darkColor = {
  '& path': {
    fill: darkerGray,
  },
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    error: {
      color: 'white',
      backgroundColor: red,
      '&:hover': {
        backgroundColor: red,
        opacity: 0.9,
      },
    },
    errorOutlined: {
      color: red,
      borderColor: red,
      '&:hover': {
        color: 'white',
        borderColor: red,
        backgroundColor: red,
      },
    },
    errorText: {
      color: red,
      borderColor: 'transparent',
      '&:hover': {
        //color: 'white',
        borderColor: 'transparent',
        backgroundColor: 'transparent',
      },
    },
    // Loader variants using composition
    loader: {
      ...sidePosition,
      ...loaderAnimation,
      ...lightColor,
    },
    loaderDark: {
      ...sidePosition,
      ...loaderAnimation,
      ...darkColor,
    },
    loaderMiddle: {
      ...centerPosition,
      ...loaderAnimation,
      ...lightColor,
    },
    loaderMiddleDark: {
      ...centerPosition,
      ...loaderAnimation,
      ...darkColor,
    },
    // Compact loader variants for immersive mode.
    loaderMiddleSmall: {
      ...centerPositionSmall,
      ...loaderAnimation,
      ...lightColor,
    },
    loaderMiddleDarkSmall: {
      ...centerPositionSmall,
      ...loaderAnimation,
      ...darkColor,
    },
    disabledNoOpacity: {
      backgroundColor: (props: YumButtonProps) =>
        props.variant === 'outlined' ? 'white !important' : '#E0E0E0 !important',
      // Ensure hover styles do not introduce opacity.
      '&:hover': {
        backgroundColor: (props: YumButtonProps) =>
          props.variant === 'outlined' ? 'white !important' : '#E0E0E0 !important',
      },
    },
  }),
)

const YumButton = (props: YumButtonProps) => {
  const classes = useStyles(props)
  // Provide default values for loader-related props
  const {
    to,
    showLoader = false,
    immersiveLoader = false,
    immersiveLoaderSize = 'default',
    darkLoader = false,
    disableDisabledOpacity = false,
    disabled,
    color,
    component,
    variant,
    ...otherProps
  } = props
  //const noChildren = props.children === false

  // Choose the appropriate loader class based on position and color
  const getLoaderClass = () => {
    if (immersiveLoader) {
      // Use a smaller centered loader when requested for compact buttons.
      if (immersiveLoaderSize === 'small') {
        return darkLoader ? classes.loaderMiddleDarkSmall : classes.loaderMiddleSmall
      }
      return darkLoader ? classes.loaderMiddleDark : classes.loaderMiddle
    } else {
      return darkLoader ? classes.loaderDark : classes.loader
    }
  }

  return (
    <Button
      size="medium"
      variant={variant || 'contained'}
      color={!color || color === 'error' ? 'primary' : color}
      disabled={disabled}
      disableElevation
      disableRipple
      // @ts-ignore: see https://github.com/mui-org/material-ui/issues/7877
      component={component ? component : to ? Link : undefined}
      to={to}
      classes={{
        root:
          color === 'error'
            ? variant === 'text'
              ? classes.errorText
              : variant === 'outlined'
                ? classes.errorOutlined
                : classes.error
            : undefined,
        disabled: disableDisabledOpacity ? classes.disabledNoOpacity : undefined,
      }}
      fullWidth={otherProps.size !== 'small'}
      {...otherProps}
    >
      {disabled && showLoader && <LoadIcon className={getLoaderClass()} />}
      {immersiveLoader && showLoader ? <span style={{ opacity: 0 }}>{props.children}</span> : props.children}
    </Button>
  )
}

export default YumButton
